"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Order, OrderStatus, PaymentStatus } from "@/types/orders/orders";
import { Design, HiVis } from "@/types/editor/blocks";
import { SimplePricingTier } from "@/types/products/pricing-tier";
import { updateHiVisDesign } from "@/lib/hivis-manager";
import { DesignFileDownloads } from "@/components/orders/design-file-downloads";
import { ProductFileDownloads } from "@/components/orders/product-file-downloads";
import { DesignFile } from "@/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "next-auth/react";
import { Select, SelectOption } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Package,
  Truck,
  FileText,
  CreditCard,
  Home,
  Calendar,
  Clipboard,
  Download,
  Box,
} from "lucide-react";
import {
  OrderStatusEnum,
  PaymentStatusEnum,
} from "@/lib/validations/orders/orders";
import { SaveOrderDialog } from "./save-order-dialog";
import DownloadCMYKPDF from "@/components/cc/download-cmyk-pdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { generateSVG } from "@/components/cc/download-cmyk-pdf";
import { Product } from "@/types/products/product";
import OrderSummaryTable from "@/components/orders/order-summary-table";
import { CartItem } from "@/types/orders/cart";
import { CompanyWithRelations } from "@/types";
import Image from "next/image";

// Add helper function to convert Design to HiVis
const convertDesignToHiVis = (
  design: Design,
  product: Product,
  decalNumber?: string
): HiVis => {
  // Ensure decalNumber is valid and not empty
  const validDecalNumber = decalNumber?.trim() || "";

  // Create the basic HiVis object using the design directly
  const hiVis: HiVis = {
    id: design.id || "",
    assetNumber: validDecalNumber || "AFS-01", // Will only use AFS-01 if decalNumber is empty
    gap: [3, 3, 3, 3, 3, 3],
    naturalWidth: true,
    stackFirstTwo: false,
    design: design, // Use the design directly without modifications
  };

  // Update the HiVis design with the characters from the asset number
  const updatedHiVis = updateHiVisDesign(hiVis);

  return updatedHiVis;
};

// Define interface for custom data
interface CustomData {
  decalNumber?: string;
}

interface OrderFormProps {
  order?: Order;
  pricingTiers: SimplePricingTier[];
  customerCompany?: CompanyWithRelations | null;
  onSubmit: (data: Order) => Promise<Order>;
  onCancel: () => void;
  onAddNote: (
    orderId: string,
    note: string,
    userId: string,
    type?: "internal" | "customer"
  ) => Promise<Order>;
  onSendUpdateEmail: (
    orderId: string,
    updateMessage?: string
  ) => Promise<{ data: { success: boolean; error: string | null } }>;
  onGetDesign: (params: { productId: string }) => Promise<{
    data: { success: boolean; error?: string; data?: { design: Design } };
  }>;
  onGetProductFiles: (params: { productIds: string[] }) => Promise<{
    data: {
      success: boolean;
      error?: string;
      data?: {
        productFiles: Record<
          string,
          {
            pdf_attachments: { name: string; url: string }[];
            design_files: { name: string; url: string }[];
          }
        >;
      };
    };
  }>;
  productDesignFiles?: Record<string, DesignFile[]>;
}

export default function OrderForm({
  order,
  pricingTiers,
  customerCompany,
  onSubmit,
  onCancel,
  onAddNote,
  onSendUpdateEmail,
  onGetDesign,
  onGetProductFiles,
  productDesignFiles = {},
}: OrderFormProps) {
  const { toast } = useToast();
  const { data: session } = useSession();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(order || ({} as Order));
  const [activeTab, setActiveTab] = useState("customer");
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState<"internal" | "customer">("internal");
  const [loadingDesigns, setLoadingDesigns] = useState<Record<string, HiVis>>(
    {}
  );
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<string | null>(null);
  const [loadingDesignIds, setLoadingDesignIds] = useState<Set<string>>(
    new Set()
  );
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [productFiles, setProductFiles] = useState<
    Record<
      string,
      {
        pdf_attachments: { name: string; url: string }[];
        design_files: { name: string; url: string }[];
      }
    >
  >({});
  const [loadingProductFiles, setLoadingProductFiles] = useState<Set<string>>(
    new Set()
  );

  // Helper to find tier name - uses pricingTiers to avoid linter warning
  const getTierName = useCallback(
    (tierId: string): string => {
      const tier = pricingTiers.find((t) => t.id === tierId);
      return tier?.name || "Standard";
    },
    [pricingTiers]
  );

  // Track if there are any changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(order);

  // Note type options for the select component
  const noteTypeOptions: SelectOption[] = [
    { value: "internal", label: "Internal" },
    { value: "customer", label: "Customer" },
  ];

  // Helper function to get decal number from custom data
  const getCustomDataDecalNumber = useCallback(
    (
      customData: string | { decalNumber?: string } | undefined
    ): string | undefined => {
      if (!customData) return undefined;

      if (typeof customData === "string") {
        try {
          // Check if it's a JSON string
          if (customData.startsWith("{") && customData.endsWith("}")) {
            const parsed = JSON.parse(customData);
            return parsed?.decalNumber || undefined;
          }
          // Not JSON, return as is if not empty
          return customData.trim() !== "" ? customData : undefined;
        } catch (error) {
          console.error("Error parsing custom_data:", error);
          // Try to extract decalNumber from string in case it's a plain string
          return customData.includes("decalNumber")
            ? customData.split("decalNumber")[1].split('"')[2]
            : customData.trim() !== ""
              ? customData
              : undefined;
        }
      }

      // Handle object format
      const decalNumber = (customData as CustomData)?.decalNumber;
      return decalNumber ? decalNumber.trim() : undefined;
    },
    []
  );

  // Helper function to determine if a product is custom and needs a design download
  const isCustomProduct = useCallback(
    (item: NonNullable<Order["cart"]>["cart_items"][0]): boolean => {
      // Check if product is custom based on part number or other indicators
      if (
        item.part_number &&
        (item.part_number.startsWith("DS-") || // Design Sign products
          item.part_number.includes("Custom") ||
          (item.title && item.title.toLowerCase().includes("custom")) ||
          item.decal_number) // Has a decal number
      ) {
        return true;
      }

      // Also check for items with custom data (like UNITED01, etc.)
      // These items need custom design generation
      const customData = getCustomDataDecalNumber(item.decal_number);
      if (customData && customData.trim() !== "") {
        console.log(
          `🎯 Found custom product with custom data: ${item.part_number} - ${customData}`
        );
        return true;
      }

      return false;
    },
    [getCustomDataDecalNumber]
  );

  // Helper function to get design files for a product
  const getProductDesignFiles = useCallback(
    (productId: string): DesignFile[] => {
      return productDesignFiles[productId] || [];
    },
    [productDesignFiles]
  );

  // Helper function to load product files (PDF attachments and design files)
  const loadProductFiles = useCallback(
    async (productIds: string[]) => {
      if (productIds.length === 0) return;

      // Filter out already loaded products
      const unloadedIds = productIds.filter((id) => !productFiles[id]);
      if (unloadedIds.length === 0) return;

      // Mark as loading
      setLoadingProductFiles((prev) => {
        const updated = new Set(prev);
        unloadedIds.forEach((id) => updated.add(id));
        return updated;
      });

      try {
        const result = await onGetProductFiles({ productIds: unloadedIds });

        if (result?.data?.success && result.data.data?.productFiles) {
          setProductFiles((prev) => ({
            ...prev,
            ...result.data.data!.productFiles,
          }));
        }
      } catch (error) {
        console.error("Error loading product files:", error);
      } finally {
        // Remove from loading set
        setLoadingProductFiles((prev) => {
          const updated = new Set(prev);
          unloadedIds.forEach((id) => updated.delete(id));
          return updated;
        });
      }
    },
    [productFiles, onGetProductFiles]
  );

  // Helper function to check if a product has files to download
  const hasProductFiles = useCallback(
    (productId: string): boolean => {
      const files = productFiles[productId];
      if (!files) return false;

      return (
        files.pdf_attachments?.length > 0 || files.design_files?.length > 0
      );
    },
    [productFiles]
  );

  // Helper function to get all files for a product (combining PDF attachments and design files)
  const getProductFiles = useCallback(
    (
      productId: string
    ): { name: string; url: string; type: "pdf" | "design" }[] => {
      const files = productFiles[productId];
      if (!files) return [];

      const allFiles: { name: string; url: string; type: "pdf" | "design" }[] =
        [];

      files.pdf_attachments?.forEach((file) => {
        allFiles.push({ ...file, type: "pdf" });
      });

      files.design_files?.forEach((file) => {
        allFiles.push({ ...file, type: "design" });
      });

      return allFiles;
    },
    [productFiles]
  );

  // Add a function to load design data for a specific item
  const loadDesignForItem = useCallback(
    async (item: NonNullable<Order["cart"]>["cart_items"][0]) => {
      if (!isCustomProduct(item)) return null;

      // If already loading this design, return null to avoid duplicate requests
      if (loadingDesignIds.has(item.product_id)) return null;

      // If we already have this design loaded, return it immediately
      if (loadingDesigns[item.product_id]) {
        return loadingDesigns[item.product_id];
      }

      // Mark this design as loading
      setLoadingDesignIds((prev) => {
        const updated = new Set(prev);
        updated.add(item.product_id);
        return updated;
      });

      let decalNumber = getCustomDataDecalNumber(item.decal_number);

      // If no decal number is found, use a default based on the part number
      if (!decalNumber || decalNumber.trim() === "") {
        decalNumber = item.part_number || "AFS-DEFAULT";
      }

      try {
        // Fetch the design data
        const result = await onGetDesign({
          productId: item.product_id,
        });

        if (!result?.data?.success || !result.data.data?.design) {
          throw new Error(result?.data?.error || "Failed to fetch design");
        }

        // Get the design data
        const designData = result.data.data.design;

        // Create the HiVis object with the decal number
        const hiVis = convertDesignToHiVis(
          designData,
          item as unknown as Product,
          decalNumber
        );

        // Store the updated HiVis in state
        setLoadingDesigns((prev) => ({
          ...prev,
          [item.product_id]: hiVis,
        }));

        return hiVis;
      } catch (error) {
        console.error("Error loading design:", error);
        return null;
      } finally {
        // Remove this design from the loading set regardless of success/failure
        setLoadingDesignIds((prev) => {
          const updated = new Set(prev);
          updated.delete(item.product_id);
          return updated;
        });
      }
    },
    [
      isCustomProduct,
      getCustomDataDecalNumber,
      onGetDesign,
      loadingDesigns,
      loadingDesignIds,
    ]
  );

  // Add useEffect to load designs when the order changes
  useEffect(() => {
    if (order?.cart?.cart_items) {
      // Find all custom products that need designs
      const customItems = order.cart.cart_items.filter(isCustomProduct);

      // Pre-load designs for the first few items (limit to 3 to avoid too many requests)
      const itemsToPreload = customItems.slice(0, 3);

      itemsToPreload.forEach((item) => {
        loadDesignForItem(item).catch((err) =>
          console.error(`Failed to preload design for ${item.product_id}:`, err)
        );
      });
    }
  }, [order, isCustomProduct, loadDesignForItem]);

  // Add useEffect to load product files when the order changes
  useEffect(() => {
    if (order?.cart?.cart_items) {
      // Get all unique product IDs (including attachments)
      const allProductIds = new Set<string>();

      order.cart.cart_items.forEach((item) => {
        allProductIds.add(item.product_id);

        // Add attachment product IDs
        if (item.attachments) {
          item.attachments.forEach((attachment) => {
            allProductIds.add(attachment.product_id);
          });
        }
      });

      // Load product files for all products
      if (allProductIds.size > 0) {
        loadProductFiles(Array.from(allProductIds)).catch((err) =>
          console.error("Failed to preload product files:", err)
        );
      }
    }
  }, [order, loadProductFiles]);

  if (!order) {
    return (
      <div className='flex items-center justify-center h-40'>
        <p className='text-muted-foreground'>Select an order to view details</p>
      </div>
    );
  }

  const handleChange = (
    name: keyof Order,
    value: string | number | boolean | Date | null
  ) => {
    console.log("🔄 OrderForm handleChange:", {
      name,
      value,
      currentFormData: formData,
    });
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (sendEmail: boolean, emailMessage?: string) => {
    try {
      setIsSubmitting(true);

      // Only include the editable fields that can be changed through the form
      const changedFields: Partial<Order> = {
        id: order?.id,
      };

      let hasChanges = false;

      // Check specific editable fields for changes
      if (formData.status !== order?.status) {
        changedFields.status = formData.status;
        hasChanges = true;
      }

      if (formData.payment_status !== order?.payment_status) {
        changedFields.payment_status = formData.payment_status;
        hasChanges = true;
      }

      if (formData.shipping_method !== order?.shipping_method) {
        changedFields.shipping_method = formData.shipping_method;
        hasChanges = true;
      }

      if (formData.tracking_number !== order?.tracking_number) {
        changedFields.tracking_number = formData.tracking_number;
        hasChanges = true;
      }

      if (formData.freight_account_details !== order?.freight_account_details) {
        changedFields.freight_account_details =
          formData.freight_account_details;
        hasChanges = true;
      }

      if (
        formData.requires_shipping_contact !== order?.requires_shipping_contact
      ) {
        changedFields.requires_shipping_contact =
          formData.requires_shipping_contact;
        hasChanges = true;
      }

      if (
        formData.estimated_completion_date !== order?.estimated_completion_date
      ) {
        changedFields.estimated_completion_date =
          formData.estimated_completion_date;
        hasChanges = true;
      }

      if (formData.priority !== order?.priority) {
        changedFields.priority = formData.priority;
        hasChanges = true;
      }

      console.log("💾 OrderForm handleSave:", {
        formData,
        originalOrder: order,
        changedFields,
        hasChanges,
      });

      // Only submit if there are actual changes
      if (!hasChanges) {
        console.log("📝 No changes detected, skipping update");
        return formData;
      }

      const result = await onSubmit(changedFields as Order);

      // If requested, send the email notification
      if (sendEmail) {
        try {
          // Only send the email, don't try to add a note if the message is empty
          const emailResult = await onSendUpdateEmail(
            order.id,
            emailMessage?.trim() || undefined
          );
          if (!emailResult?.data?.success) {
            throw new Error(emailResult?.data?.error || "Failed to send email");
          }
        } catch (emailError) {
          console.error("Error sending email:", emailError);
          // Don't throw the error here, just show a toast notification
          toast({
            title: "Email notification issue",
            description:
              "Order was saved but there was an issue with the email notification.",
            variant: "destructive",
          });
        }
      }

      toast({
        title: "Order updated",
        description:
          formData.status !== order?.status
            ? `Order status changed to ${formatStatus(formData.status)}`
            : "The order has been successfully updated.",
      });

      // Update only the fields that were changed, keep the rest of the form data intact
      setFormData((prev) => ({
        ...prev,
        ...result,
        updated: new Date(), // Update the timestamp to reflect the change
      }));
    } catch (error) {
      console.error("Error updating order:", error);
      toast({
        title: "Update failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update the order. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !session?.user?.id) return;

    try {
      const updatedOrder = await onAddNote(
        order.id,
        newNote,
        session.user.id,
        noteType
      );

      // Update the local form data with the new notes
      setFormData((prev) => ({
        ...prev,
        notes: updatedOrder.notes,
        internal_notes: updatedOrder.internal_notes,
      }));

      toast({
        title: "Note added",
        description: "Your note has been added to the order.",
      });
      setNewNote("");
    } catch (error) {
      console.error("Error adding note:", error);
      toast({
        title: "Failed to add note",
        description: "There was an error adding your note. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Format the status for display
  const formatStatus = (status: OrderStatus) => {
    return status
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Format the payment status for display
  const formatPaymentStatus = (status: PaymentStatus) => {
    return status
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get available statuses from OrderStatusEnum
  const availableStatuses = Object.values(OrderStatusEnum.enum);

  // Convert statuses to SelectOption format
  const statusOptions: SelectOption[] = availableStatuses.map((status) => ({
    value: status,
    label: formatStatus(status as OrderStatus),
  }));

  // Get current status as SelectOption
  const selectedStatusOption =
    statusOptions.find((option) => option.value === formData.status) || null;

  // Get available payment statuses from PaymentStatusEnum
  const availablePaymentStatuses = Object.values(PaymentStatusEnum.enum);

  // Helper function to get emoji for payment status
  const getPaymentStatusEmoji = (status: PaymentStatus) => {
    const emojiMap: Record<PaymentStatus, string> = {
      succeeded: "🤑",
      pending: "⏳",
      failed: "⛔",
      refunded: "💀",
      processing: "🕙",
    };
    return emojiMap[status] || "";
  };

  // Convert payment statuses to SelectOption format
  const paymentStatusOptions: SelectOption[] = availablePaymentStatuses.map(
    (status) => ({
      value: status,
      label: `${getPaymentStatusEmoji(status as PaymentStatus)} ${formatPaymentStatus(status as PaymentStatus)}`,
    })
  );

  // Get current payment status as SelectOption
  const selectedPaymentStatusOption =
    paymentStatusOptions.find(
      (option) => option.value === formData.payment_status
    ) || null;

  // Helper function to safely extract file extension and name
  const getFileNameParts = (fileName: string, fileUrl?: string) => {
    if (!fileName || typeof fileName !== "string") {
      return { baseName: "unknown_file", extension: "bin" };
    }

    // Remove any path components and get just the filename
    const cleanFileName = fileName.split("/").pop() || fileName;

    // Check if file has an extension
    const lastDotIndex = cleanFileName.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      // No extension in filename, try to extract from URL
      if (fileUrl) {
        try {
          // Extract filename from URL
          const urlPath = new URL(fileUrl).pathname;
          const urlFileName = urlPath.split("/").pop() || "";
          const urlLastDotIndex = urlFileName.lastIndexOf(".");

          if (urlLastDotIndex > 0) {
            const extensionFromUrl = urlFileName.substring(urlLastDotIndex + 1);
            console.log(
              `📎 Extracted extension "${extensionFromUrl}" from URL for file "${fileName}"`
            );
            return {
              baseName: cleanFileName || "unknown_file",
              extension: extensionFromUrl,
            };
          }
        } catch (error) {
          console.warn(`Failed to parse URL: ${fileUrl}`, error);
        }
      }

      // No extension found anywhere, use default
      return { baseName: cleanFileName || "unknown_file", extension: "bin" };
    }

    const extension = cleanFileName.substring(lastDotIndex + 1);
    const baseName = cleanFileName.substring(0, lastDotIndex);

    return {
      baseName: baseName || "unknown_file",
      extension: extension || "bin",
    };
  };

  // Helper function to sanitize filename for ZIP
  const sanitizeFileName = (fileName: string) => {
    return fileName
      .replace(/[<>:"/\\|?*]/g, "_") // Replace invalid characters
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .replace(/_{2,}/g, "_") // Replace multiple underscores with single
      .substring(0, 200); // Limit length
  };

  // Function to handle downloading all designs as a ZIP
  const downloadAllDesignsAsZip = async () => {
    setIsLoadingAll(true);
    setDownloadProgress("Loading designs for ZIP...");

    try {
      // Get all custom products first
      const customItems = (order.cart?.cart_items || []).filter((item) =>
        isCustomProduct(item)
      );

      // Get all items with design files (from productDesignFiles prop)
      const itemsWithDesignFiles = (order.cart?.cart_items || []).filter(
        (item) => {
          const designFiles = getProductDesignFiles(item.product_id);
          return designFiles.length > 0;
        }
      );

      // Get all items with product files (PDF attachments and design files from database)
      const itemsWithProductFiles = (order.cart?.cart_items || []).filter(
        (item) => hasProductFiles(item.product_id)
      );

      // Get all attachment items with product files
      const attachmentItemsWithFiles: Array<{
        item: NonNullable<Order["cart"]>["cart_items"][0];
        attachment: NonNullable<
          NonNullable<Order["cart"]>["cart_items"][0]["attachments"]
        >[0];
      }> = [];

      (order.cart?.cart_items || []).forEach((item) => {
        if (item.attachments) {
          item.attachments.forEach((attachment) => {
            if (hasProductFiles(attachment.product_id)) {
              attachmentItemsWithFiles.push({ item, attachment });
            }
          });
        }
      });

      if (
        customItems.length === 0 &&
        itemsWithDesignFiles.length === 0 &&
        itemsWithProductFiles.length === 0 &&
        attachmentItemsWithFiles.length === 0
      ) {
        toast({
          title: "No files to download",
          description:
            "There are no custom products with designs, design files, or PDF attachments in this order.",
        });
        setIsLoadingAll(false);
        setDownloadProgress(null);
        return;
      }

      // Create a new JSZip instance
      const zip = new JSZip();
      let processedCount = 0;
      const totalItems =
        customItems.length +
        itemsWithDesignFiles.length +
        itemsWithProductFiles.length +
        attachmentItemsWithFiles.length;

      console.log(
        `📦 Starting ZIP creation with ${totalItems} items to process`
      );

      // Process each custom product sequentially to avoid race conditions
      for (let i = 0; i < customItems.length; i++) {
        const item = customItems[i];
        processedCount++;

        try {
          // Get the decal number for this item
          let decalNumber = getCustomDataDecalNumber(item.decal_number);
          if (!decalNumber || decalNumber.trim() === "") {
            decalNumber = item.part_number || "AFS-DEFAULT";
          }

          setDownloadProgress(
            `Processing custom design ${processedCount}/${totalItems}: ${item.part_number} (${decalNumber})...`
          );

          console.log(`🎨 Processing custom design for ${item.part_number}`);

          // Get the design for this product using loadDesignForItem instead of handleGetDesign
          const design = await loadDesignForItem(item);

          if (!design || !design.design?.blocks?.length) {
            console.error(
              `No valid design found for ${item.title} (${item.product_id})`
            );
            continue; // Skip this item and continue with the next
          }

          // Generate PDF for this design
          const rootBlock = design.design.blocks[0];
          const needsBleed =
            rootBlock.blocks?.[0]?.color === "#000000" &&
            (rootBlock.blocks[0].width === rootBlock.width ||
              rootBlock.blocks[0].height === rootBlock.height);

          // Always use dimensions directly from the processed HiVis rootBlock
          // This ensures we get the dimensions after all transformations
          const designWidthMM = rootBlock.width;
          const designHeightMM = rootBlock.height;

          // Add bleed only if needed (exactly 2mm on each side = 4mm total)
          const bleedWidthMM = needsBleed ? designWidthMM + 4 : designWidthMM;
          const bleedHeightMM = needsBleed
            ? designHeightMM + 4
            : designHeightMM;
          const MM_TO_PT = 2.83465;
          const widthPT = bleedWidthMM * MM_TO_PT;
          const heightPT = bleedHeightMM * MM_TO_PT;

          // Generate filename with proper sanitization
          const sanitizedOrderNumber = sanitizeFileName(
            order.order_number || "ORDER"
          );
          const sanitizedPartNumber = sanitizeFileName(
            item.part_number || "PART"
          );
          const sanitizedDecalNumber = sanitizeFileName(decalNumber);
          const fileName = `${sanitizedOrderNumber}_${sanitizedPartNumber}_${sanitizedDecalNumber}.pdf`;

          setDownloadProgress(`Creating PDF for ${item.part_number}...`);

          // Generate SVG content
          const svgContent = generateSVG(
            rootBlock,
            bleedWidthMM,
            bleedHeightMM,
            design,
            {
              useNaturalWidth: design.naturalWidth,
            }
          );

          // Import and use generateSVGPDF directly within the loop
          const { generateSVGPDF } = await import(
            "@/server/actions/pdf/generate-pdf"
          );

          // Generate PDF and convert to bytes
          setDownloadProgress(`Converting ${fileName} to PDF...`);
          const base64PDF = await generateSVGPDF(svgContent, widthPT, heightPT);

          // Convert base64 to ArrayBuffer
          const binaryString = atob(base64PDF);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          console.log(
            `✅ Adding custom PDF to ZIP: ${fileName} (${bytes.length} bytes)`
          );
          zip.file(fileName, bytes);
        } catch (itemError) {
          console.error(`Error processing item ${item.title}:`, itemError);
          // Continue with next item
        }
      }

      // Process design files for each product
      for (let i = 0; i < itemsWithDesignFiles.length; i++) {
        const item = itemsWithDesignFiles[i];
        processedCount++;

        try {
          setDownloadProgress(
            `Processing design files ${processedCount}/${totalItems}: ${item.part_number}...`
          );

          const designFiles = getProductDesignFiles(item.product_id);
          console.log(
            `📄 Processing ${designFiles.length} design files for ${item.part_number}`
          );

          for (let j = 0; j < designFiles.length; j++) {
            const designFile = designFiles[j];

            try {
              console.log(
                `📥 Fetching design file: ${designFile.name} from ${designFile.url}`
              );

              // Fetch the file
              const response = await fetch(designFile.url);
              if (!response.ok) {
                throw new Error(
                  `Failed to fetch ${designFile.name} - ${response.status}: ${response.statusText}`
                );
              }

              const fileBlob = await response.blob();
              const arrayBuffer = await fileBlob.arrayBuffer();

              // Create filename with product info using helper function
              const { baseName, extension } = getFileNameParts(
                designFile.name,
                designFile.url
              );
              const sanitizedOrderNumber = sanitizeFileName(
                order.order_number || "ORDER"
              );
              const sanitizedPartNumber = sanitizeFileName(
                item.part_number || "PART"
              );
              const sanitizedBaseName = sanitizeFileName(baseName);
              const fileName = `${sanitizedOrderNumber}_${sanitizedPartNumber}_DesignFile_${sanitizedBaseName}.${extension}`;

              console.log(
                `✅ Adding design file to ZIP: ${fileName} (${arrayBuffer.byteLength} bytes)`
              );
              zip.file(fileName, arrayBuffer);
            } catch (fileError) {
              console.error(
                `Error processing design file ${designFile.name}:`,
                fileError
              );
              // Continue with next file
            }
          }
        } catch (itemError) {
          console.error(
            `Error processing design files for ${item.title}:`,
            itemError
          );
          // Continue with next item
        }
      }

      // Process items with product files (PDF attachments and design files from database)
      for (let i = 0; i < itemsWithProductFiles.length; i++) {
        const item = itemsWithProductFiles[i];
        processedCount++;

        try {
          setDownloadProgress(
            `Processing product files ${processedCount}/${totalItems}: ${item.part_number}...`
          );

          const files = getProductFiles(item.product_id);
          console.log(
            `📎 Processing ${files.length} product files for ${item.part_number}`
          );

          for (const file of files) {
            try {
              console.log(
                `📥 Fetching product file: ${file.name} from ${file.url}`
              );

              // Fetch the file
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(
                  `Failed to fetch ${file.name} - ${response.status}: ${response.statusText}`
                );
              }

              const fileBlob = await response.blob();
              const arrayBuffer = await fileBlob.arrayBuffer();

              // Create filename with product info and file type using helper function
              const { baseName, extension } = getFileNameParts(
                file.name,
                file.url
              );
              const sanitizedOrderNumber = sanitizeFileName(
                order.order_number || "ORDER"
              );
              const sanitizedPartNumber = sanitizeFileName(
                item.part_number || "PART"
              );
              const sanitizedBaseName = sanitizeFileName(baseName);
              const typePrefix = file.type === "pdf" ? "PDF" : "Design";
              const fileName = `${sanitizedOrderNumber}_${sanitizedPartNumber}_${typePrefix}_${sanitizedBaseName}.${extension}`;

              console.log(
                `✅ Adding product file to ZIP: ${fileName} (${arrayBuffer.byteLength} bytes)`
              );
              zip.file(fileName, arrayBuffer);
            } catch (fileError) {
              console.error(
                `Error processing product file ${file.name}:`,
                fileError
              );
              // Continue with next file
            }
          }
        } catch (itemError) {
          console.error(
            `Error processing product files for ${item.title}:`,
            itemError
          );
          // Continue with next item
        }
      }

      // Process attachment items with product files
      for (let i = 0; i < attachmentItemsWithFiles.length; i++) {
        const { item, attachment } = attachmentItemsWithFiles[i];
        processedCount++;

        try {
          setDownloadProgress(
            `Processing attachment files ${processedCount}/${totalItems}: ${attachment.part_number}...`
          );

          const files = getProductFiles(attachment.product_id);
          console.log(
            `🔗 Processing ${files.length} attachment files for ${attachment.part_number}`
          );

          for (const file of files) {
            try {
              console.log(
                `📥 Fetching attachment file: ${file.name} from ${file.url}`
              );

              // Fetch the file
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(
                  `Failed to fetch ${file.name} - ${response.status}: ${response.statusText}`
                );
              }

              const fileBlob = await response.blob();
              const arrayBuffer = await fileBlob.arrayBuffer();

              // Create filename with main product info, attachment info, and file type using helper function
              const { baseName, extension } = getFileNameParts(
                file.name,
                file.url
              );
              const sanitizedOrderNumber = sanitizeFileName(
                order.order_number || "ORDER"
              );
              const sanitizedPartNumber = sanitizeFileName(
                item.part_number || "PART"
              );
              const sanitizedAttachmentPartNumber = sanitizeFileName(
                attachment.part_number || "ATTACHMENT"
              );
              const sanitizedBaseName = sanitizeFileName(baseName);
              const typePrefix = file.type === "pdf" ? "PDF" : "Design";
              const fileName = `${sanitizedOrderNumber}_${sanitizedPartNumber}_Attachment_${sanitizedAttachmentPartNumber}_${typePrefix}_${sanitizedBaseName}.${extension}`;

              console.log(
                `✅ Adding attachment file to ZIP: ${fileName} (${arrayBuffer.byteLength} bytes)`
              );
              zip.file(fileName, arrayBuffer);
            } catch (fileError) {
              console.error(
                `Error processing attachment file ${file.name}:`,
                fileError
              );
              // Continue with next file
            }
          }
        } catch (itemError) {
          console.error(
            `Error processing attachment files for ${attachment.title}:`,
            itemError
          );
          // Continue with next item
        }
      }

      const fileCount = Object.keys(zip.files).length;
      console.log(
        `📦 ZIP contains ${fileCount} files:`,
        Object.keys(zip.files)
      );

      if (fileCount === 0) {
        toast({
          title: "No files to download",
          description:
            "Could not generate any valid designs, design files, or PDF attachments for download.",
          variant: "destructive",
        });
        setIsLoadingAll(false);
        setDownloadProgress(null);
        return;
      }

      // Generate the ZIP file
      setDownloadProgress(`Creating ZIP file with ${fileCount} files...`);
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      // Save the ZIP file with sanitized filename
      const sanitizedOrderNumber = sanitizeFileName(
        order.order_number || "ORDER"
      );
      const zipFileName = `${sanitizedOrderNumber}_designs_and_files.zip`;
      saveAs(zipBlob, zipFileName);

      console.log(`✅ ZIP file created successfully: ${zipFileName}`);

      // Show success message
      toast({
        title: "ZIP Downloaded",
        description: `Successfully created ZIP with ${fileCount} files including custom designs, design files, PDF attachments, and attachment product files.`,
      });
    } catch (downloadError) {
      console.error("Error creating ZIP file:", downloadError);
      toast({
        title: "Error",
        description: "Failed to create ZIP file. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingAll(false);
      setDownloadProgress(null);
    }
  };

  return (
    <div className='space-y-6 px-4'>
      {/* Status and Completion Date - Inline */}
      <div className='mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Label htmlFor='status'>Order Status</Label>
            <Select
              options={statusOptions}
              selected={selectedStatusOption}
              onChange={(option) => {
                if (option && !Array.isArray(option)) {
                  handleChange("status", option.value as OrderStatus);
                }
              }}
              placeholder='Select status'
              className='w-full mt-1'
            />
          </div>
          <div>
            <Label htmlFor='estimated_completion_date'>
              Estimated Completion Date
            </Label>
            <Input
              id='estimated_completion_date'
              type='date'
              className='mt-1'
              value={
                formData.estimated_completion_date
                  ? new Date(formData.estimated_completion_date)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={(e) =>
                handleChange(
                  "estimated_completion_date",
                  e.target.value ? new Date(e.target.value) : null
                )
              }
            />
          </div>
        </div>
      </div>

      <Tabs
        defaultValue='details'
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className='grid grid-cols-6'>
          <TabsTrigger value='customer'>Customer</TabsTrigger>
          <TabsTrigger value='items'>
            Items ({order.cart?.cart_items?.length || 0})
          </TabsTrigger>
          <TabsTrigger value='shipping'>Shipping</TabsTrigger>
          <TabsTrigger value='payment'>Payment</TabsTrigger>
          <TabsTrigger value='timeline'>Timeline</TabsTrigger>
          <TabsTrigger value='notes'>Notes</TabsTrigger>
        </TabsList>

        <TabsContent value='notes' className='space-y-4 mt-4'>
          <Card>
            <CardHeader>
              <CardTitle className='text-base flex items-center'>
                <Clipboard className='h-4 w-4 mr-2' />
                Order Notes
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div>
                <div className='mb-2'>
                  <Label htmlFor='note-type'>Note Type:</Label>
                  <div className='mt-1'>
                    <Select
                      options={noteTypeOptions}
                      selected={
                        noteTypeOptions.find(
                          (option) => option.value === noteType
                        ) || null
                      }
                      onChange={(option) => {
                        if (option && !Array.isArray(option)) {
                          setNoteType(option.value as "internal" | "customer");
                        }
                      }}
                      placeholder='Select type'
                      className='h-10'
                    />
                  </div>
                </div>

                <Textarea
                  placeholder='Add a note about this order...'
                  value={newNote}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setNewNote(e.target.value)
                  }
                  className='min-h-[100px]'
                />

                <Button
                  onClick={handleAddNote}
                  className='mt-2'
                  disabled={!newNote.trim()}
                >
                  Add Note
                </Button>
              </div>

              <Separator />

              <div>
                <h4 className='font-medium mb-2'>Customer Notes</h4>
                {formData.notes ? (
                  <div className='border rounded-md p-4'>
                    <p className='text-sm whitespace-pre-wrap'>
                      {formData.notes}
                    </p>
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No customer notes for this order
                  </p>
                )}
              </div>

              <div>
                <h4 className='font-medium mb-2'>Internal Notes</h4>
                {formData.internal_notes ? (
                  <div className='border rounded-md p-4'>
                    <p className='text-sm whitespace-pre-wrap'>
                      {formData.internal_notes}
                    </p>
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No internal notes for this order
                  </p>
                )}
              </div>

              {formData.engineering_notes && (
                <div>
                  <h4 className='font-medium mb-2'>Engineering Notes</h4>
                  <div className='border rounded-md p-4 bg-purple-50'>
                    <p className='text-sm whitespace-pre-wrap'>
                      {formData.engineering_notes}
                    </p>
                  </div>
                </div>
              )}

              {formData.production_notes && (
                <div>
                  <h4 className='font-medium mb-2'>Production Notes</h4>
                  <div className='border rounded-md p-4 bg-green-50'>
                    <p className='text-sm whitespace-pre-wrap'>
                      {formData.production_notes}
                    </p>
                  </div>
                </div>
              )}

              {formData.quality_notes && (
                <div>
                  <h4 className='font-medium mb-2'>Quality Notes</h4>
                  <div className='border rounded-md p-4 bg-orange-50'>
                    <p className='text-sm whitespace-pre-wrap'>
                      {formData.quality_notes}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='items' className='space-y-4 mt-4'>
          <Card>
            <CardHeader>
              <div className='flex justify-between items-center'>
                <div className='flex items-center gap-2'>
                  <Package className='h-4 w-4' />
                  <CardTitle className='text-base'>Order Items</CardTitle>
                </div>
                {/* Show the download button if there are any downloadable files */}
                {order.cart?.cart_items &&
                  order.cart.cart_items.length > 0 &&
                  (order.cart.cart_items.some(isCustomProduct) ||
                    order.cart.cart_items.some((item) => {
                      const designFiles = getProductDesignFiles(
                        item.product_id
                      );
                      return designFiles.length > 0;
                    }) ||
                    order.cart.cart_items.some((item) =>
                      hasProductFiles(item.product_id)
                    ) ||
                    order.cart.cart_items.some((item) =>
                      item.attachments?.some((attachment) =>
                        hasProductFiles(attachment.product_id)
                      )
                    )) && (
                    <Button
                      variant='default'
                      size='sm'
                      onClick={downloadAllDesignsAsZip}
                      disabled={isLoadingAll}
                      title='Download all custom designs, design files, and PDF attachments as a single ZIP file'
                    >
                      {isLoadingAll && downloadProgress ? (
                        downloadProgress
                      ) : (
                        <>
                          <Download className='h-4 w-4 mr-2' />
                          Download All Files
                        </>
                      )}
                    </Button>
                  )}
              </div>
              <CardDescription>
                {order.cart?.cart_items?.length || 0} items in this order
              </CardDescription>
            </CardHeader>
            <CardContent>
              {order.cart?.cart_items && order.cart.cart_items.length > 0 ? (
                <OrderSummaryTable
                  cartItems={order.cart.cart_items as CartItem[]}
                  showImage={true}
                  showPartNumber={true}
                  showQuantity={true}
                  showPrice={true}
                  showActions={true}
                  showPackageInfo={true}
                  showItemIds={true}
                  renderItemActions={(item: CartItem) => {
                    // Use tierName without displaying it (to avoid linter warning)
                    void getTierName(item.tier_id || "");

                    const designFiles = getProductDesignFiles(item.product_id);
                    const hasDesignFiles = designFiles.length > 0;
                    const isCustom = isCustomProduct(item);
                    const hasFiles = hasProductFiles(item.product_id);
                    const isLoadingFiles = loadingProductFiles.has(
                      item.product_id
                    );

                    // If no files available, return null
                    if (!isCustom && !hasDesignFiles && !hasFiles) return null;

                    return (
                      <div className='flex items-center gap-2'>
                        {/* Design Files Download Button (from productDesignFiles prop) */}
                        {hasDesignFiles && (
                          <DesignFileDownloads
                            designFiles={designFiles}
                            productTitle={item.title}
                          />
                        )}

                        {/* Product Files Download Button (PDF attachments and design files from database) */}
                        {hasFiles && !isLoadingFiles && (
                          <ProductFileDownloads
                            files={getProductFiles(item.product_id)}
                            productTitle={item.title}
                          />
                        )}

                        {/* Loading Product Files */}
                        {isLoadingFiles && (
                          <Button variant='outline' size='sm' disabled>
                            <span className='animate-pulse'>
                              Loading files...
                            </span>
                          </Button>
                        )}

                        {/* Custom Design Download Button */}
                        {isCustom && (
                          <>
                            {/* If design is loading */}
                            {loadingDesignIds.has(item.product_id) && (
                              <Button variant='outline' size='sm' disabled>
                                <span className='animate-pulse'>
                                  Loading design...
                                </span>
                              </Button>
                            )}

                            {/* If design is loaded */}
                            {loadingDesigns[item.product_id] &&
                              !loadingDesignIds.has(item.product_id) && (
                                <DownloadCMYKPDF
                                  debugMode={false}
                                  hiVis={loadingDesigns[item.product_id]}
                                  orderNumber={order.order_number}
                                  partNumber={item.part_number}
                                  decalNumber={getCustomDataDecalNumber(
                                    item.decal_number
                                  )}
                                />
                              )}

                            {/* Default - load design button */}
                            {!loadingDesigns[item.product_id] &&
                              !loadingDesignIds.has(item.product_id) && (
                                <Button
                                  variant='outline'
                                  size='sm'
                                  onClick={() => loadDesignForItem(item)}
                                  className='flex items-center justify-center'
                                  title='Load Design'
                                >
                                  <Box className='h-4 w-4' />
                                </Button>
                              )}
                          </>
                        )}
                      </div>
                    );
                  }}
                />
              ) : (
                <p className='text-muted-foreground'>No items in this order</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='shipping' className='space-y-4 mt-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base flex items-center'>
                  <Home className='h-4 w-4 mr-2' />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.shipping_address ? (
                  <div className='space-y-3'>
                    <div className='border-l-4 border-primary pl-4'>
                      <div className='space-y-1'>
                        {order.shipping_address.name && (
                          <p className='font-medium text-lg'>
                            {order.shipping_address.name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground uppercase tracking-wide'>
                        Address
                      </Label>
                      {order.shipping_address.address1 && (
                        <p className='text-sm'>
                          {order.shipping_address.address1}
                        </p>
                      )}
                      {order.shipping_address.address2 && (
                        <p className='text-sm text-muted-foreground'>
                          {order.shipping_address.address2}
                        </p>
                      )}
                      <p className='text-sm'>
                        {[
                          order.shipping_address.city,
                          order.shipping_address.state,
                          order.shipping_address.postal_code,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {order.shipping_address.country && (
                        <p className='text-sm font-medium'>
                          {order.shipping_address.country}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-8 text-muted-foreground'>
                    <Home className='h-8 w-8 mx-auto mb-2 opacity-50' />
                    <p>No shipping address provided</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base flex items-center'>
                  <Truck className='h-4 w-4 mr-2' />
                  Shipping Information
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <Label htmlFor='shipping_method'>Shipping Method</Label>
                  <Input
                    id='shipping_method'
                    value={formData.shipping_method || ""}
                    onChange={(e) =>
                      handleChange("shipping_method", e.target.value)
                    }
                    className='mt-1'
                    placeholder='e.g., Standard Shipping, Express'
                  />
                </div>

                <div>
                  <Label htmlFor='tracking_number'>Tracking Number</Label>
                  <Input
                    id='tracking_number'
                    value={formData.tracking_number || ""}
                    onChange={(e) =>
                      handleChange("tracking_number", e.target.value)
                    }
                    className='mt-1'
                    placeholder='Enter tracking number'
                  />
                </div>

                <div>
                  <Label htmlFor='freight_account_details'>
                    Freight Account Details
                  </Label>
                  <Textarea
                    id='freight_account_details'
                    value={formData.freight_account_details || ""}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      handleChange("freight_account_details", e.target.value)
                    }
                    className='mt-1'
                    placeholder='Enter freight account details if applicable'
                  />
                </div>

                <div className='flex items-center space-x-2'>
                  <input
                    type='checkbox'
                    id='requires_shipping_contact'
                    checked={formData.requires_shipping_contact || false}
                    onChange={(e) =>
                      handleChange(
                        "requires_shipping_contact",
                        e.target.checked
                      )
                    }
                    className='rounded border-gray-300'
                  />
                  <Label htmlFor='requires_shipping_contact'>
                    Requires shipping contact before delivery
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value='payment' className='space-y-4 mt-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base flex items-center'>
                  <CreditCard className='h-4 w-4 mr-2' />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <Label htmlFor='payment_status'>Payment Status</Label>
                      <div className='mt-1'>
                        <Select
                          options={paymentStatusOptions}
                          selected={selectedPaymentStatusOption}
                          onChange={(option) => {
                            if (option && !Array.isArray(option)) {
                              handleChange(
                                "payment_status",
                                option.value as PaymentStatus
                              );
                            }
                          }}
                          placeholder='Select payment status'
                          className='h-10'
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Total</Label>
                      <p className='text-2xl font-medium'>
                        ${Number(order.total).toFixed(2)}
                      </p>
                      {/* Show price breakdown if available */}
                      {order.subtotal && (
                        <div className='mt-2 text-muted-foreground space-y-1'>
                          <div className='flex justify-between'>
                            <span>Subtotal:</span>
                            <span>${Number(order.subtotal).toFixed(2)}</span>
                          </div>
                          {order.shipping_price &&
                            Number(order.shipping_price) > 0 && (
                              <div className='flex justify-between'>
                                <span>Shipping:</span>
                                <span>
                                  ${Number(order.shipping_price).toFixed(2)}
                                </span>
                              </div>
                            )}
                          {order.gst_amount && (
                            <div className='flex justify-between'>
                              <span>GST (10%):</span>
                              <span>
                                ${Number(order.gst_amount).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {order.card_surcharge &&
                            Number(order.card_surcharge) > 0 && (
                              <div className='flex justify-between'>
                                <span>Card Surcharge:</span>
                                <span>
                                  ${Number(order.card_surcharge).toFixed(2)}
                                </span>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>

                  {order.purchase_order_number && (
                    <div>
                      <Label>Purchase Order Number</Label>
                      <p className='text-2xl font-medium'>
                        {order.purchase_order_number}
                      </p>
                    </div>
                  )}

                  {order.purchase_order_url && (
                    <div>
                      <div className='mt-2'>
                        <Button
                          variant='default'
                          onClick={() =>
                            order.purchase_order_url &&
                            window.open(order.purchase_order_url, "_blank")
                          }
                          className='flex items-center gap-2'
                        >
                          <FileText className='h-4 w-4' />
                          View Purchase Order
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base flex items-center'>
                  <FileText className='h-4 w-4 mr-2' />
                  Billing Address
                  {order.shipping_address &&
                    order.billing_address &&
                    order.billing_address.address1 ===
                      order.shipping_address.address1 &&
                    order.billing_address.city ===
                      order.shipping_address.city && (
                      <p className='text-xs text-primary font-medium pl-2'>
                        * Same as shipping address
                      </p>
                    )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.billing_address ? (
                  <div className='space-y-3'>
                    <div className='border-l-4 border-green-500 pl-4'>
                      <div className='space-y-1'>
                        {order.billing_address.name && (
                          <p className='font-medium text-lg'>
                            {order.billing_address.name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground uppercase tracking-wide'>
                        Address
                      </Label>
                      {order.billing_address.address1 && (
                        <p className='text-sm'>
                          {order.billing_address.address1}
                        </p>
                      )}
                      {order.billing_address.address2 && (
                        <p className='text-sm text-muted-foreground'>
                          {order.billing_address.address2}
                        </p>
                      )}
                      <p className='text-sm'>
                        {[
                          order.billing_address.city,
                          order.billing_address.state,
                          order.billing_address.postal_code,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {order.billing_address.country && (
                        <p className='text-sm font-medium'>
                          {order.billing_address.country}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-8 text-muted-foreground'>
                    <FileText className='h-8 w-8 mx-auto mb-2 opacity-50' />
                    <p>No billing address provided</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value='timeline' className='space-y-4 mt-4'>
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-base flex items-center'>
                <Calendar className='h-4 w-4 mr-2' />
                Order Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className='max-h-60 overflow-y-auto'>
              <div className='space-y-2'>
                {order.status_history ? (
                  order.status_history.map((history, index) => (
                    <div key={index} className='flex items-start'>
                      <div className='mr-2 h-4 w-4 flex items-center justify-center mt-1'>
                        <div className='h-2 w-2 rounded-full bg-primary'></div>
                      </div>
                      <div className='flex-1'>
                        <p className='text-sm font-medium'>
                          {formatStatus(history.new_status)}
                          {history.previous_status && (
                            <span className='font-normal text-muted-foreground'>
                              {" "}
                              from {formatStatus(history.previous_status)}
                            </span>
                          )}
                        </p>
                        {history.notes && (
                          <p className='text-xs text-muted-foreground'>
                            {history.notes}
                          </p>
                        )}
                        <p className='text-xs text-muted-foreground'>
                          {formatDate(history.created)} •{" "}
                          {history.changed_by || "System"}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No status history available
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='customer' className='space-y-6 mt-4'>
          {/* Customer Information Card */}
          <Card className='overflow-hidden'>
            <CardHeader className='border-b'>
              <div className='flex items-center justify-between'>
                <CardTitle className='text-lg flex items-center gap-3'>
                  <div className='relative'>
                    <Avatar className='h-12 w-12 border-2 border-background shadow-sm'>
                      <AvatarImage
                        src={order.user?.image || undefined}
                        alt='Customer avatar'
                        className='object-cover'
                      />
                      <AvatarFallback className='text-lg font-bold bg-primary/10'>
                        {(() => {
                          const cartUserData = order.cart?.user_data as
                            | {
                                firstName?: string;
                                first_name?: string;
                                lastName?: string;
                                last_name?: string;
                              }
                            | undefined;
                          const firstName =
                            order.user?.firstName ||
                            cartUserData?.firstName ||
                            cartUserData?.first_name;
                          const lastName =
                            order.user?.lastName ||
                            cartUserData?.lastName ||
                            cartUserData?.last_name;

                          if (firstName || lastName) {
                            const initials =
                              `${firstName?.[0] || ""}${lastName?.[0] || ""}`.trim();
                            return initials || "U";
                          }
                          return "U";
                        })()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div>
                    <div className='font-semibold'>
                      {(() => {
                        const cartUserData = order.cart?.user_data as
                          | {
                              firstName?: string;
                              first_name?: string;
                              lastName?: string;
                              last_name?: string;
                            }
                          | undefined;
                        const firstName =
                          order.user?.firstName ||
                          cartUserData?.firstName ||
                          cartUserData?.first_name;
                        const lastName =
                          order.user?.lastName ||
                          cartUserData?.lastName ||
                          cartUserData?.last_name;

                        if (firstName || lastName) {
                          return `${firstName || ""} ${lastName || ""}`.trim();
                        }
                        return "Customer";
                      })()}
                    </div>
                    <div className='text-sm text-muted-foreground font-normal'>
                      {order.user?.role ? (
                        <span className='capitalize'>{order.user.role}</span>
                      ) : (
                        "Customer"
                      )}
                    </div>
                  </div>
                </CardTitle>
                {(order.user?.id || order.cart?.user_data?.id) && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      const userId =
                        order.user?.id || order.cart?.user_data?.id;
                      if (userId) {
                        window.open(`/dashboard/users/${userId}`, "_blank");
                      }
                    }}
                    className='shadow-md'
                  >
                    <FileText className='h-4 w-4 mr-2' />
                    View Profile
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className='p-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='space-y-1'>
                  <Label className='text-xs text-muted-foreground'>
                    Email Address
                  </Label>
                  <p className='text-sm'>
                    {(() => {
                      const cartUserData = order.cart?.user_data as
                        | { email?: string }
                        | undefined;
                      const email = order.user?.email || cartUserData?.email;
                      return email ? (
                        <a
                          href={`mailto:${email}`}
                          className='text-primary hover:text-primary/80 transition-colors'
                        >
                          {email}
                        </a>
                      ) : (
                        <span className='text-muted-foreground'>
                          No email provided
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <div className='space-y-1'>
                  <Label className='text-xs text-muted-foreground'>
                    Phone Number
                  </Label>
                  <p className='text-sm'>
                    {(() => {
                      const cartUserData = order.cart?.user_data as
                        | { phone?: string }
                        | undefined;
                      const phone = order.user?.phone || cartUserData?.phone;
                      return phone ? (
                        <a
                          href={`tel:${phone}`}
                          className='text-primary hover:text-primary/80 transition-colors'
                        >
                          {phone}
                        </a>
                      ) : (
                        <span className='text-muted-foreground'>
                          No phone provided
                        </span>
                      );
                    })()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Information Card */}
          {customerCompany ? (
            <Card className='overflow-hidden'>
              <CardHeader className='border-b'>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-lg flex items-center gap-3'>
                    <div className='relative'>
                      <div className='h-12 w-12 rounded-lg overflow-hidden bg-background border-2 border-border shadow-sm flex items-center justify-center'>
                        {customerCompany.logo ? (
                          <Image
                            src={customerCompany.logo}
                            alt={`${customerCompany.name} logo`}
                            className='h-full w-full object-contain bg-white p-1'
                            fill
                            sizes="48px"
                            style={{ objectFit: "contain" }}
                            priority
                          />
                        ) : (
                          <div className='text-lg font-bold text-primary'>
                            {customerCompany.name?.[0]?.toUpperCase() || "C"}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className='font-semibold'>
                        {customerCompany.name}
                      </div>
                      <div className='text-sm text-muted-foreground font-normal'>
                        Company Details
                      </div>
                    </div>
                  </CardTitle>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() =>
                      window.open(
                        `/dashboard/companies/${customerCompany.id}`,
                        "_blank"
                      )
                    }
                    className='shadow-md'
                  >
                    View Company
                  </Button>
                </div>
              </CardHeader>
              <CardContent className='p-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6'>
                  {customerCompany.website && (
                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground'>
                        Website
                      </Label>
                      <p className='text-sm'>
                        <a
                          href={
                            customerCompany.website.startsWith("http")
                              ? customerCompany.website
                              : `https://${customerCompany.website}`
                          }
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary hover:text-primary/80 underline transition-colors'
                        >
                          {customerCompany.website}
                        </a>
                      </p>
                    </div>
                  )}
                  {customerCompany.email && (
                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground'>
                        Email
                      </Label>
                      <p className='text-sm'>
                        <a
                          href={`mailto:${customerCompany.email}`}
                          className='text-primary hover:text-primary/80 transition-colors'
                        >
                          {customerCompany.email}
                        </a>
                      </p>
                    </div>
                  )}
                  {customerCompany.cell && (
                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground'>
                        Phone
                      </Label>
                      <p className='text-sm'>
                        <a
                          href={`tel:${customerCompany.cell}`}
                          className='text-primary hover:text-primary/80 transition-colors'
                        >
                          {customerCompany.cell}
                        </a>
                      </p>
                    </div>
                  )}
                  {customerCompany.business_number && (
                    <div className='space-y-1'>
                      <Label className='text-xs text-muted-foreground'>
                        Business Number
                      </Label>
                      <p className='text-sm'>
                        {customerCompany.business_number}
                      </p>
                    </div>
                  )}
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Pricing Tier
                    </Label>
                    <p className='text-sm'>
                      {(() => {
                        // Find tier by ID from the company's pricing_tier_id
                        const companyTier = customerCompany.pricing_tier_id
                          ? pricingTiers.find(
                              (tier) =>
                                tier.id === customerCompany.pricing_tier_id
                            )
                          : null;

                        if (companyTier) {
                          return companyTier.name;
                        }
                        // Default to Tier 3 if no tier found
                        const defaultTier =
                          pricingTiers.find((tier) =>
                            tier.name.toLowerCase().includes("tier 3")
                          ) ||
                          pricingTiers.find((tier) =>
                            tier.name.toLowerCase().includes("retail")
                          ) ||
                          pricingTiers[2]; // Fallback to third tier if exists
                        return defaultTier?.name || "Tier 3 (Default)";
                      })()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className='border-dashed border-2'>
              <CardContent className='p-8 text-center'>
                <div className='space-y-4'>
                  <div className='h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center'>
                    <div className='text-2xl font-bold text-muted-foreground'>
                      {(() => {
                        const cartUserData = order.cart?.user_data as
                          | { company?: string; companyName?: string }
                          | undefined;
                        const companyName =
                          cartUserData?.company || cartUserData?.companyName;
                        return companyName?.[0]?.toUpperCase() || "C";
                      })()}
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <h4 className='font-medium'>
                      {(() => {
                        const cartUserData = order.cart?.user_data as
                          | { company?: string; companyName?: string }
                          | undefined;
                        return (
                          cartUserData?.company ||
                          cartUserData?.companyName ||
                          "Individual Customer"
                        );
                      })()}
                    </h4>
                    <p className='text-sm text-muted-foreground'>
                      {(() => {
                        const cartUserData = order.cart?.user_data as
                          | { company?: string; companyName?: string }
                          | undefined;
                        const hasCompany =
                          cartUserData?.company || cartUserData?.companyName;
                        if (hasCompany) {
                          return "No detailed company information available";
                        }
                        return "This customer is not associated with a company";
                      })()}
                    </p>
                    <div className='mt-3'>
                      {(() => {
                        // Default to Tier 3 for individual customers
                        const defaultTier =
                          pricingTiers.find((tier) =>
                            tier.name.toLowerCase().includes("tier-3")
                          ) ||
                          pricingTiers[2]; // Fallback to third tier if exists
                        return (
                          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground'>
                            {defaultTier?.name || "Tier 3 (Default)"}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Sticky Save/Cancel Bar */}
      <div className='sticky bottom-0 z-40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t -mx-4 md:mx-0 pt-4 px-4 md:px-6'>
        {/* Desktop layout */}
        <div className='hidden md:flex items-center justify-between py-4'>
          <div className='text-sm text-muted-foreground space-x-4'>
            {order?.created && (
              <span>Created: {formatDate(order.created)}</span>
            )}
            {order?.updated && (
              <span>Updated: {formatDate(order.updated)}</span>
            )}
          </div>
          <div className='flex space-x-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                if (hasChanges) {
                  setShowCancelDialog(true);
                } else {
                  onCancel();
                }
              }}
            >
              Close
            </Button>
            <SaveOrderDialog
              order={order}
              isSubmitting={isSubmitting}
              hasChanges={hasChanges}
              onSave={async (sendEmail: boolean, emailMessage?: string) => {
                try {
                  setIsSubmitting(true);
                  await handleSave(sendEmail, emailMessage);
                } catch (error) {
                  console.error("Error saving order:", error);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            />
          </div>
        </div>

        {/* Mobile layout */}
        <div className='md:hidden space-y-4'>
          <div className='flex justify-between text-sm text-muted-foreground'>
            {order?.created && <div>Created: {formatDate(order.created)}</div>}
            {order?.updated && <div>Updated: {formatDate(order.updated)}</div>}
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                if (hasChanges) {
                  setShowCancelDialog(true);
                } else {
                  onCancel();
                }
              }}
              className='w-full'
            >
              Close
            </Button>
            <SaveOrderDialog
              order={order}
              isSubmitting={isSubmitting}
              hasChanges={hasChanges}
              onSave={async (sendEmail: boolean, emailMessage?: string) => {
                try {
                  setIsSubmitting(true);
                  await handleSave(sendEmail, emailMessage);
                } catch (error) {
                  console.error("Error saving order:", error);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard your changes? All unsaved changes
              will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
